import React, { useState, useEffect, useRef } from 'react';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import Layout from '../components/Layout';
import { PageKey } from '../components/Layout';
import { getAccountDetail, getPushHistory, updateAccountSettings,
  getWpConfig, saveWpConfig, getRealLeads, autodetectWp } from '../api';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Campaign {
  id: string; name: string; status: string;
  daily_budget: number; leads: number; impressions: number; clicks: number; ctr: number;
  created_time?: string;
}
interface Location {
  location?: string; location_key?: string; radius_km?: number;
}
interface CustomerSettings {
  _key?: string; account_id?: string;
  location?: string; location_key?: string; location_country?: string;
  radius_km?: number; gender?: string;
  pixel_id?: string; pixel_name?: string;
  page_id?: string; page_name?: string;
  impressum_url?: string; soka_ad_url?: string;
  wordpress_url?: string;
  landingpage_url?: string; display_url?: string; cta?: string;
  locations?: Location[];
}
interface DayData { date: string; label: string; impressions: number; leads: number; }
interface AccountData {
  account: { id: string; name: string; business_name?: string };
  pages: { id: string; name: string }[];
  instagram: { id: string; name: string; username: string }[];
  active_campaigns: Campaign[];
  paused_campaigns: Campaign[];
  daily_chart: DayData[];
  daily_chart_7d?: DayData[];
  customer_settings: CustomerSettings;
}
interface PushEntry { ts: string; campaign_name: string; status: string; job_id: string; }

interface Props {
  accountId: string; accountName: string;
  user: { email: string; name: string };
  onLogout: () => void; onBack: () => void; onNavigate: (page: PageKey) => void;
}

// ─── Formatters ───────────────────────────────────────────────────────────────

const fmt = (n: number) => new Intl.NumberFormat('de-DE').format(n);
const fmtBudget = (c: number) => c > 0 ? `${Math.round(c / 100)}€/Tag` : '–';
const fmtTs = (ts: string) => {
  try {
    const d = new Date(ts);
    return `${String(d.getDate()).padStart(2,'0')}.${String(d.getMonth()+1).padStart(2,'0')}.${d.getFullYear()} ${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
  } catch { return ts; }
};

const daysSince = (iso?: string): string | null => {
  if (!iso) return null;
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (days <= 0) return 'Heute';
  if (days === 1) return '1 Tag';
  return `${days} Tage`;
};

// Required fields — shown red when empty
const REQUIRED: Record<string, string> = {
  location_key: 'Ort-Key', pixel_id: 'Pixel-ID',
  page_id: 'Facebook-Seite', impressum_url: 'Impressum-URL', soka_ad_url: 'Webseite-URL',
};

// ─── Chart tooltip ────────────────────────────────────────────────────────────

const ChartTip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: '#fff', border: '1px solid #e8e8ed', borderRadius: 8, padding: '8px 12px', fontSize: 12 }}>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>{label}</div>
      {payload.map((p: any) => (
        <div key={p.name} style={{ color: p.color }}>
          {p.name}: <strong>{p.name === 'Leads' ? p.value : fmt(p.value)}</strong>
        </div>
      ))}
    </div>
  );
};

// ─── Campaign table ───────────────────────────────────────────────────────────

function CampTable({ campaigns, faded, showDuration }: { campaigns: Campaign[]; faded?: boolean; showDuration?: boolean }) {
  const headers = showDuration
    ? ['Kampagne', 'Leads', 'Impressionen', 'Klicks', 'CTR', 'Budget', 'Online seit', 'Status']
    : ['Kampagne', 'Leads', 'Impressionen', 'Klicks', 'CTR', 'Budget', 'Status'];
  return (
    <table style={{ ...s.table, opacity: faded ? 0.65 : 1 }}>
      <thead>
        <tr>
          {headers.map(h => (
            <th key={h} style={{ ...s.th, textAlign: h === 'Kampagne' ? 'left' : 'right' as any }}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {campaigns.map((c, i) => (
          <tr key={c.id} style={{ background: i % 2 === 1 ? '#fafafa' : 'transparent' }}>
            <td style={s.tdName}>{c.name}</td>
            <td style={{ ...s.td, color: c.leads > 0 ? '#1a3a5c' : '#aeaeb2', fontWeight: c.leads > 0 ? 700 : 400 }}>{c.leads}</td>
            <td style={s.td}>{fmt(c.impressions)}</td>
            <td style={s.td}>{fmt(c.clicks)}</td>
            <td style={s.td}>{c.ctr.toFixed(2)}%</td>
            <td style={s.td}>{fmtBudget(c.daily_budget)}</td>
            {showDuration && (
              <td style={s.td}>
                <span style={{ fontSize: 11, color: '#6e6e73' }}>
                  {daysSince(c.created_time) ? `Seit ${daysSince(c.created_time)}` : '–'}
                </span>
              </td>
            )}
            <td style={s.td}>
              <span style={{
                fontSize: 11, padding: '2px 7px', borderRadius: 5, fontWeight: 600,
                background: c.status === 'ACTIVE' ? 'rgba(52,199,89,0.12)' : '#f5f5f7',
                color: c.status === 'ACTIVE' ? '#34c759' : '#aeaeb2',
              }}>
                {c.status === 'ACTIVE' ? 'Aktiv' : 'Pausiert'}
              </span>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ─── Kundendaten read-only view ───────────────────────────────────────────────

function KundendatenView({ cs }: { cs: CustomerSettings }) {
  const hasLocations = (cs.locations?.length ?? 0) > 0;

  const rows: Array<{ key: keyof CustomerSettings; label: string; url?: boolean; fmtFn?: (v: any) => string }> = [
    ...(!hasLocations ? [
      { key: 'location' as keyof CustomerSettings, label: 'Ort' },
      { key: 'location_key' as keyof CustomerSettings, label: 'Ort-Key' },
      { key: 'radius_km' as keyof CustomerSettings, label: 'Radius', fmtFn: (v: any) => `${v} km` },
    ] : []),
    { key: 'gender', label: 'Geschlecht' },
    { key: 'pixel_id', label: 'Pixel-ID' },
    { key: 'page_id', label: 'Seiten-ID' },
    { key: 'impressum_url', label: 'Impressum-URL', url: true },
    { key: 'soka_ad_url', label: 'Webseite-URL', url: true },
    { key: 'cta', label: 'CTA-Button' },
    { key: 'wordpress_url', label: 'WordPress-URL', url: true },
  ];

  return (
    <div style={s.kvList}>
      {hasLocations && (
        <div style={s.kvRow}>
          <span style={s.kvLabel}>Standorte</span>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end' }}>
            {cs.locations!.map((loc, i) => (
              <div key={i} style={{ textAlign: 'right' as const, fontSize: 13 }}>
                <span style={{ fontWeight: 500, color: '#1d1d1f' }}>{loc.location || '—'}</span>
                {loc.radius_km ? <span style={{ color: '#6e6e73', marginLeft: 5 }}> · {loc.radius_km} km</span> : null}
                {loc.location_key ? <span style={{ color: '#aeaeb2', fontSize: 11, marginLeft: 5 }}>({loc.location_key})</span> : null}
              </div>
            ))}
          </div>
        </div>
      )}
      {rows.map(({ key, label, url, fmtFn }) => {
        const val = cs[key];
        const missing = (key in REQUIRED) && !val;
        return (
          <div key={key} style={s.kvRow}>
            <span style={s.kvLabel}>{label}</span>
            <span style={{ ...s.kvVal, ...(missing ? s.kvMissing : {}) }}>
              {val
                ? (url
                  ? <a href={String(val)} target="_blank" rel="noreferrer" style={{ color: '#1a3a5c', textDecoration: 'none', wordBreak: 'break-all' }}>{String(val)}</a>
                  : fmtFn ? fmtFn(val) : String(val))
                : <span style={{ color: missing ? '#ff3b30' : '#aeaeb2' }}>{missing ? '⚠ Fehlt' : '—'}</span>
              }
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ─── Kundendaten edit form ────────────────────────────────────────────────────

function KundendatenEdit({ fields, onChange, onSave, onCancel, saving, saveError }: {
  fields: CustomerSettings; onChange: (f: CustomerSettings) => void;
  onSave: () => void; onCancel: () => void; saving: boolean; saveError: string;
}) {
  const set = (k: keyof CustomerSettings, v: any) => onChange({ ...fields, [k]: v });

  const EDIT_ROWS: Array<{ key: keyof CustomerSettings; label: string; type?: string; opts?: string[] }> = [
    { key: 'gender', label: 'Geschlecht', opts: ['alle', 'frauen', 'männer'] },
    { key: 'pixel_id', label: 'Pixel-ID', type: 'text' },
    { key: 'page_id', label: 'Facebook-Seite ID', type: 'text' },
    { key: 'impressum_url', label: 'Impressum-URL', type: 'url' },
    { key: 'soka_ad_url', label: 'Webseite-URL', type: 'url' },
    { key: 'cta', label: 'CTA-Button', opts: ['SIGN_UP', 'LEARN_MORE', 'GET_QUOTE', 'CONTACT_US', 'SUBSCRIBE'] },
    { key: 'wordpress_url', label: 'WordPress-URL', type: 'url' },
  ];

  const locations = fields.locations || [];
  const updateLoc = (idx: number, patch: Partial<Location>) => {
    const locs = [...locations];
    locs[idx] = { ...locs[idx], ...patch };
    onChange({ ...fields, locations: locs });
  };
  const addLoc = () => onChange({ ...fields, locations: [...locations, { location: '', location_key: '', radius_km: 25 }] });
  const removeLoc = (idx: number) => onChange({ ...fields, locations: locations.filter((_, i) => i !== idx) });

  return (
    <div>
      {/* Standorte */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: '#6e6e73', textTransform: 'uppercase' as const, letterSpacing: '0.05em' }}>Standorte</span>
          <button type="button" onClick={addLoc} style={s.addLocBtn}>+ Standort</button>
        </div>
        {locations.length === 0 && (
          <div style={{ fontSize: 12, color: '#aeaeb2', marginBottom: 8 }}>Noch keine Standorte — verwende globale Felder unten oder füge Standorte hinzu.</div>
        )}
        {locations.map((loc, idx) => (
          <div key={idx} style={{ display: 'flex', gap: 6, marginBottom: 8, alignItems: 'flex-end' }}>
            <div style={{ flex: 2 }}>
              <label style={s.editLabel}>Ort</label>
              <input type="text" value={loc.location || ''} onChange={e => updateLoc(idx, { location: e.target.value })} style={s.editInput} />
            </div>
            <div style={{ flex: 2 }}>
              <label style={s.editLabel}>Ort-Key</label>
              <input type="text" value={loc.location_key || ''} onChange={e => updateLoc(idx, { location_key: e.target.value })} style={s.editInput} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={s.editLabel}>Radius</label>
              <input type="number" value={loc.radius_km ?? ''} onChange={e => updateLoc(idx, { radius_km: Number(e.target.value) })} style={s.editInput} />
            </div>
            <button type="button" onClick={() => removeLoc(idx)} style={s.removeLocBtn}>−</button>
          </div>
        ))}
      </div>

      <div style={{ borderTop: '1px solid #f0f0f5', paddingTop: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
        {EDIT_ROWS.map(({ key, label, type, opts }) => {
          const val = fields[key] ?? '';
          const required = key in REQUIRED;
          const missing = required && !val;
          return (
            <div key={key}>
              <label style={s.editLabel}>
                {label}
                {required && <span style={{ color: '#ff3b30', marginLeft: 2 }}>*</span>}
              </label>
              {opts ? (
                <select value={String(val)} onChange={e => set(key, e.target.value)} style={s.editInput}>
                  {opts.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : (
                <input
                  type={type || 'text'}
                  value={String(val)}
                  onChange={e => set(key, e.target.value)}
                  style={{ ...s.editInput, ...(missing ? { borderColor: '#ff3b30', background: '#fff8f8' } : {}) }}
                />
              )}
            </div>
          );
        })}
      </div>
      {saveError && <div style={{ color: '#ff3b30', fontSize: 12, marginTop: 8 }}>{saveError}</div>}
      <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
        <button onClick={onSave} disabled={saving} style={s.saveBtn}>
          {saving ? 'Speichert…' : 'Speichern'}
        </button>
        <button onClick={onCancel} style={s.cancelBtn}>Abbrechen</button>
      </div>
    </div>
  );
}

// ─── WordPress / FluentForms section ──────────────────────────────────────────

interface RealLead {
  campaign_id: string; campaign_name: string; spend: number;
  meta_form_leads: number; fluentform_leads: number; total_leads: number;
  real_cpl: number | null; form_id: string | null; has_form_mapping: boolean;
}

function WordPressSection({ accountId }: { accountId: string }) {
  const [cfg, setCfg] = useState<any>(null);
  const [form, setForm] = useState({ wp_url: '', wp_user: '', wp_app_password: '' });
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState('');
  const [leads, setLeads] = useState<RealLead[] | null>(null);
  const [showSecret, setShowSecret] = useState(false);
  const [userUnlocked, setUserUnlocked] = useState(false);

  const loadCfg = () => getWpConfig(accountId).then(r => {
    setCfg(r.data);
    setForm(f => ({ ...f, wp_url: r.data.wp_url || '', wp_user: r.data.wp_user || '' }));
  }).catch(() => {});
  const loadLeads = () => getRealLeads(accountId).then(r => setLeads(r.data.campaigns || []))
    .catch(() => setLeads([]));

  useEffect(() => { loadCfg(); loadLeads(); /* eslint-disable-next-line */ }, [accountId]);

  const save = async () => {
    setBusy('save'); setMsg('');
    try {
      const r = await saveWpConfig(accountId, form);
      setCfg(r.data);
      setForm(f => ({ ...f, wp_app_password: '' }));
      setUserUnlocked(false);
      const su = r.data.setup;
      if (su) {
        if (su.wp_status === 'connected') {
          setMsg(`✓ Verbunden · ${su.imported} Einträge importiert · ${su.mapped} Kampagnen zugeordnet`);
          loadLeads();
        } else {
          setMsg(su.wp_status_msg || 'Verbindung fehlgeschlagen');
        }
      } else {
        setMsg('Verbindung fehlgeschlagen – bitte URL prüfen');
      }
    } catch { setMsg('Verbindung fehlgeschlagen'); } finally { setBusy(''); }
  };

  const autodetect = async () => {
    setBusy('detect'); setMsg('');
    try {
      const r = await autodetectWp(accountId);
      if (r.data.ok) {
        await loadCfg();
        const su = r.data.setup;
        if (su?.wp_status === 'connected') {
          setMsg(`✓ ${r.data.detected_url} erkannt · ${su.imported} importiert · ${su.mapped} zugeordnet`);
          loadLeads();
        } else {
          setMsg(`URL erkannt (${r.data.detected_url}), aber ${su?.wp_status_msg || 'Verbindung fehlgeschlagen'}`);
        }
      } else {
        setMsg(r.data.error || 'Keine URL in den Anzeigen gefunden');
      }
    } catch { setMsg('Automatische Erkennung fehlgeschlagen'); } finally { setBusy(''); }
  };

  const status: 'connected' | 'error' | 'none' =
    cfg?.has_password ? (cfg.wp_status === 'connected' ? 'connected' : (cfg.wp_status === 'error' ? 'error' : 'none')) : 'none';
  const statusColor = { connected: '#34c759', error: '#ff9500', none: '#ff3b30' }[status];
  const statusText = { connected: 'Verbunden', error: 'Verbindung fehlerhaft', none: 'Keine Verbindung' }[status];
  const webhookUrl = cfg?.wp_webhook_secret
    ? `${window.location.origin}/api/wp/webhook/${accountId}/${cfg.wp_webhook_secret}` : '';

  return (
    <div style={s.card}>
      <div style={s.cardHeader}>
        <span style={s.cardTitle}>
          <WpIcon /> WordPress / FluentForms
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600, color: statusColor }}>
          <span style={{ width: 9, height: 9, borderRadius: '50%', background: statusColor, display: 'inline-block' }} />
          {statusText}
        </span>
      </div>

      {/* Connection form */}
      {(() => {
        const adminUrl = form.wp_url
          ? (/^https?:\/\//.test(form.wp_url) ? form.wp_url : `https://${form.wp_url}`).replace(/\/+$/, '') + '/wp-admin'
          : '';
        return (
      <div style={{ display: 'flex', flexWrap: 'wrap' as const, gap: 10, marginBottom: 10 }}>
        <div style={{ flex: '1 1 240px' }}>
          <label style={s.editLabel}>WordPress-URL</label>
          <div style={{ display: 'flex', gap: 6 }}>
            <input style={{ ...s.editInput, flex: 1 }} placeholder="gz-o.gesundfit.app" value={form.wp_url}
              onChange={e => setForm({ ...form, wp_url: e.target.value })} />
            {adminUrl && (
              <a href={adminUrl} target="_blank" rel="noreferrer" style={s.openLinkBtn} title="WordPress-Admin in neuem Tab öffnen">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                  <polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/>
                </svg>
              </a>
            )}
          </div>
        </div>
        <div style={{ flex: '1 1 160px' }}>
          <label style={s.editLabel}>Benutzername (netzwerkweit)</label>
          <div style={{ display: 'flex', gap: 6 }}>
            <input style={{ ...s.editInput, flex: 1, ...(userUnlocked ? {} : { background: '#f5f5f7', color: '#aeaeb2' }) }}
              value={form.wp_user} disabled={!userUnlocked}
              onChange={e => setForm({ ...form, wp_user: e.target.value })} />
            <button type="button" onClick={() => setUserUnlocked(u => !u)} style={s.openLinkBtn}
              title={userUnlocked ? 'Sperren' : 'Zum Ändern entsperren'}>
              {userUnlocked ? '🔓' : '🔒'}
            </button>
          </div>
        </div>
        <div style={{ flex: '1 1 200px' }}>
          <label style={s.editLabel}>Application Password {cfg?.has_password && <span style={{ color: '#34c759' }}>● gespeichert</span>}</label>
          <input style={s.editInput} type="password"
            placeholder={cfg?.has_password ? '•••• (unverändert lassen)' : 'xxxx xxxx xxxx xxxx'}
            value={form.wp_app_password} onChange={e => setForm({ ...form, wp_app_password: e.target.value })} />
        </div>
      </div>
        );
      })()}

      {cfg?.uses_global_password ? (
        <div style={{ fontSize: 11.5, color: '#34c759', marginBottom: 12 }}>
          ✓ Netzwerk-Passwort wird verwendet – du musst hier nur die URL eintragen.
        </div>
      ) : !cfg?.has_password && (
        <div style={{ fontSize: 11.5, color: '#6e6e73', background: '#f5f5f7', borderRadius: 8, padding: '8px 11px', marginBottom: 12, lineHeight: 1.5 }}>
          <strong>App-Passwort anlegen (einmalig fürs ganze Netzwerk):</strong> Im WordPress → <em>Benutzer → Profil</em> → ganz unten <em>„Anwendungspasswörter"</em> → Name z.B. „PS-Brain" → <em>„Anwendungspasswort hinzufügen"</em> → Code (Format <code>xxxx xxxx xxxx xxxx</code>) hier einfügen. Gilt dann für alle Partner.
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' as const, alignItems: 'center' }}>
        <button style={s.saveBtn} disabled={busy === 'save'} onClick={save}>
          {busy === 'save' ? 'Verbinde & importiere…' : 'Verbinden & Leads abrufen'}
        </button>
        <button style={s.editBtn} disabled={busy === 'detect'} onClick={autodetect} title="WordPress-URL aus den Meta-Anzeigen automatisch erkennen">
          {busy === 'detect' ? 'Suche…' : '🔍 URL automatisch erkennen'}
        </button>
        {msg && <span style={{ fontSize: 12, color: msg.startsWith('✓') ? '#34c759' : '#ff3b30' }}>{msg}</span>}
      </div>

      {/* Webhook URL */}
      {webhookUrl && (
        <div style={{ marginTop: 14, background: '#f5f5f7', borderRadius: 10, padding: '10px 12px' }}>
          <div style={{ fontSize: 11, color: '#6e6e73', marginBottom: 4 }}>
            Webhook-URL für FluentForms (Pro → Einstellungen → Webhook), um neue Einträge automatisch zu empfangen:
          </div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <code style={{ fontSize: 11, color: '#1d1d1f', wordBreak: 'break-all' as const, flex: 1 }}>
              {showSecret ? webhookUrl : webhookUrl.replace(cfg.wp_webhook_secret, '••••••••')}
            </code>
            <button style={s.miniBtn} onClick={() => setShowSecret(s => !s)}>{showSecret ? 'verbergen' : 'zeigen'}</button>
            <button style={s.miniBtn} onClick={() => navigator.clipboard?.writeText(webhookUrl)}>kopieren</button>
          </div>
        </div>
      )}

      {/* Real leads per campaign */}
      <div style={{ marginTop: 18 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#1d1d1f', marginBottom: 8 }}>
          Echte Leads pro Kampagne <span style={{ fontSize: 11, color: '#aeaeb2', fontWeight: 400 }}>(Meta-Formular + FluentForms, ohne Pixel)</span>
        </div>
        {leads === null ? (
          <div style={s.empty}>Lade echte Leads…</div>
        ) : leads.filter(l => l.total_leads > 0 || l.has_form_mapping).length === 0 ? (
          <div style={s.empty}>Noch keine echten Leads zugeordnet. Landingpages werden beim Push automatisch erkannt; Meta-Formulare zählen direkt.</div>
        ) : (
          <table style={s.table}>
            <thead><tr>
              {['Kampagne', 'Meta-Form', 'FluentForms', 'Gesamt', 'Ausgaben', 'Echter CPL'].map((h, i) => (
                <th key={h} style={{ ...s.th, textAlign: i === 0 ? 'left' : 'right' as any }}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {leads.filter(l => l.total_leads > 0 || l.has_form_mapping).map((l, i) => (
                <tr key={l.campaign_id} style={{ background: i % 2 === 1 ? '#fafafa' : 'transparent' }}>
                  <td style={s.tdName}>{l.campaign_name}{l.form_id && <span style={{ fontSize: 10, color: '#aeaeb2', marginLeft: 6 }}>FF #{l.form_id}</span>}</td>
                  <td style={s.td}>{l.meta_form_leads || '–'}</td>
                  <td style={s.td}>{l.fluentform_leads || '–'}</td>
                  <td style={{ ...s.td, fontWeight: 700, color: l.total_leads > 0 ? '#1a3a5c' : '#aeaeb2' }}>{l.total_leads}</td>
                  <td style={s.td}>{l.spend.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</td>
                  <td style={{ ...s.td, fontWeight: 600 }}>{l.real_cpl != null ? `${l.real_cpl.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €` : '–'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function AccountDetail({ accountId, accountName, user, onLogout, onBack, onNavigate }: Props) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState<AccountData | null>(null);
  const [pushHistory, setPushHistory] = useState<PushEntry[]>([]);
  const [pausedExpanded, setPausedExpanded] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editFields, setEditFields] = useState<CustomerSettings>({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [period, setPeriod] = useState<'7d' | '30d'>('30d');
  const [wpConn, setWpConn] = useState<'connected' | 'error' | 'none'>('none');
  const kundendatenRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    getWpConfig(accountId).then(r => {
      const d = r.data;
      setWpConn(!d.has_config ? 'none' : (d.wp_status === 'connected' ? 'connected' : 'error'));
    }).catch(() => {});
  }, [accountId]);

  useEffect(() => {
    setLoading(true); setError('');
    Promise.all([
      getAccountDetail(accountId),
      getPushHistory(accountId).catch(() => ({ data: { history: [] } })),
    ])
      .then(([dr, hr]) => { setData(dr.data); setPushHistory(hr.data.history || []); })
      .catch(e => setError(e.response?.data?.error || 'Fehler beim Laden'))
      .finally(() => setLoading(false));
  }, [accountId]);

  const numericId = accountId.replace('act_', '');
  const cs = data?.customer_settings || {};
  const missingCount = Object.keys(REQUIRED).filter(f => !cs[f as keyof CustomerSettings]).length;

  const chartData = data
    ? (period === '7d' ? (data.daily_chart_7d || data.daily_chart) : data.daily_chart)
    : [];
  const totals = chartData.reduce(
    (a, c) => ({ leads: a.leads + c.leads, impressions: a.impressions + c.impressions }),
    { leads: 0, impressions: 0 }
  );

  const avgCtr = data?.active_campaigns.length
    ? (data.active_campaigns.reduce((s, c) => s + c.ctr, 0) / data.active_campaigns.length).toFixed(2)
    : '0.00';

  const startEdit = () => {
    setEditFields({ ...cs });
    setEditMode(true);
    setSaveError('');
    setTimeout(() => kundendatenRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
  };

  const saveEdit = async () => {
    setSaving(true); setSaveError('');
    try {
      await updateAccountSettings(accountId, editFields);
      setData(d => d ? { ...d, customer_settings: { ...d.customer_settings, ...editFields } } : d);
      setEditMode(false);
    } catch (e: any) {
      setSaveError(e.response?.data?.error || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const adsUrl = `https://adsmanager.facebook.com/adsmanager/manage/campaigns?act=${numericId}`;

  return (
    <Layout user={user} onLogout={onLogout} activePage="account" onNavigate={onNavigate}>
      <div style={s.page}>

        {/* ── Top bar ── */}
        <div style={s.topBar}>
          <button onClick={onBack} style={s.backBtn}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 5 }}>
              <polyline points="15 18 9 12 15 6"/>
            </svg>
            Dashboard
          </button>
          <div style={s.quickActions}>
            <button style={s.qaPrimary} onClick={() => onNavigate('push')}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 5 }}>
                <path d="M5 12h14M12 5l7 7-7 7"/>
              </svg>
              Kampagne pushen
            </button>
            {cs.wordpress_url ? (
              <a href={(/^https?:\/\//.test(cs.wordpress_url) ? cs.wordpress_url : `https://${cs.wordpress_url}`).replace(/\/+$/, '') + '/wp-admin'} target="_blank" rel="noreferrer" style={s.qaOutlineLink}>
                <WpIcon /> WordPress
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: 5 }}>
                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                  <polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/>
                </svg>
              </a>
            ) : (
              <button style={{ ...s.qaOutline, color: '#aeaeb2', cursor: 'default' }} disabled>
                <WpIcon /> WordPress
              </button>
            )}
            <a href={adsUrl} target="_blank" rel="noreferrer" style={s.qaOutlineLink}>
              Ads Manager
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: 5 }}>
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                <polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/>
              </svg>
            </a>
            <button style={s.qaOutline} onClick={startEdit}>Kundendaten bearbeiten</button>
          </div>
        </div>

        {loading && (
          <div style={s.center}>
            <div style={s.spinner}/><span style={{ color: '#6e6e73', marginLeft: 12 }}>Lade Partnerprofil…</span>
          </div>
        )}
        {error && <div style={s.errBox}>{error}</div>}

        {!loading && data && (<>

          {/* ── Partner header ── */}
          <div style={s.partnerHeader}>
            <div>
              <h1 style={s.title}>{accountName}</h1>
              <div style={s.partnerMeta}>
                <span style={s.idBadge}>{numericId}</span>
                {data.pages.slice(0, 1).map(p => (
                  <span key={p.id} style={s.fbBadge}>
                    <span style={{ fontWeight: 800, marginRight: 3 }}>f</span>{p.name}
                  </span>
                ))}
                {data.instagram.slice(0, 1).map(ig => (
                  <span key={ig.id} style={s.igBadge}>
                    <IgIcon />{ig.username ? `@${ig.username}` : ig.name}
                  </span>
                ))}
                {missingCount > 0 && (
                  <span style={s.warnBadge} onClick={startEdit} title="Fehlende Felder bearbeiten" >
                    ⚠ {missingCount} {missingCount === 1 ? 'Feld fehlt' : 'Felder fehlen'}
                  </span>
                )}
                {wpConn !== 'connected' && (
                  <span
                    style={{ ...s.wpWarnBadge, ...(wpConn === 'error' ? { color: '#c05000', background: 'rgba(255,149,0,0.12)' } : {}) }}
                    title={wpConn === 'error'
                      ? 'WordPress-Verbindung fehlerhaft – echte Lead-Zahlen evtl. nicht aktuell'
                      : 'Keine WordPress Verbindung – echte Lead-Zahlen nicht verfügbar'}
                  >
                    <span style={{ fontWeight: 800, marginRight: 4 }}>!</span>
                    {wpConn === 'error' ? 'WordPress fehlerhaft' : 'Keine WordPress-Verbindung'}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* ── Stats ── */}
          <div style={s.statsRow}>
            {[
              { label: 'Aktive Kampagnen', value: String(data.active_campaigns.length), accent: true },
              { label: `Leads (${period === '7d' ? '7 Tage' : '30 Tage'})`, value: fmt(totals.leads), accent: true },
              { label: `Impressionen (${period === '7d' ? '7 Tage' : '30 Tage'})`, value: fmt(totals.impressions) },
              { label: 'Ø CTR', value: `${avgCtr}%` },
            ].map(({ label, value, accent }) => (
              <div key={label} style={s.statCard}>
                <div style={{ ...s.statNum, ...(accent ? { color: '#1a3a5c' } : {}) }}>{value}</div>
                <div style={s.statLabel}>{label}</div>
              </div>
            ))}
          </div>

          {/* ── Graph ── */}
          {data.daily_chart.length > 0 && (
            <div style={s.card}>
              <div style={s.cardHeader}>
                <span style={s.cardTitle}>Leads & Impressionen</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={s.smallBadge}>nur dieser Partner</span>
                  <div style={{ display: 'flex', borderRadius: 8, overflow: 'hidden', border: '1px solid #e8e8ed' }}>
                    {(['7d', '30d'] as const).map(p => (
                      <button key={p} onClick={() => setPeriod(p)} style={{
                        background: period === p ? '#1a3a5c' : '#fff',
                        color: period === p ? '#fff' : '#6e6e73',
                        border: 'none', padding: '4px 12px', fontSize: 11,
                        fontWeight: 600, cursor: 'pointer',
                        borderRight: p === '7d' ? '1px solid #e8e8ed' : 'none',
                      }}>
                        {p === '7d' ? '7 Tage' : '30 Tage'}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <ResponsiveContainer width="100%" height={170}>
                <LineChart data={chartData} margin={{ top: 4, right: 44, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f5" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#aeaeb2' }} tickLine={false} axisLine={false} interval="preserveStartEnd" />
                  <YAxis yAxisId="l" tick={{ fontSize: 10, fill: '#1a3a5c' }} tickLine={false} axisLine={false} width={28} allowDecimals={false} />
                  <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 10, fill: '#aeaeb2' }} tickLine={false} axisLine={false} width={42}
                    tickFormatter={v => v >= 1000 ? `${Math.round(v / 1000)}K` : String(v)} />
                  <Tooltip content={<ChartTip />} />
                  <Line yAxisId="l" type="monotone" dataKey="leads" name="Leads" stroke="#1a3a5c" strokeWidth={2.5} dot={false} activeDot={{ r: 4 }} />
                  <Line yAxisId="r" type="monotone" dataKey="impressions" name="Impressionen" stroke="#c7d2dd" strokeWidth={1.5} dot={false} activeDot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* ── Campaign table ── */}
          <div style={s.card}>
            <div style={s.cardHeader}>
              <span style={s.cardTitle}>Aktive Kampagnen</span>
              <span style={s.countBadge}>{data.active_campaigns.length}</span>
            </div>
            {data.active_campaigns.length === 0
              ? <div style={s.empty}>Keine aktiven Kampagnen</div>
              : <CampTable campaigns={data.active_campaigns} showDuration />
            }

            {data.paused_campaigns.length > 0 && (
              <>
                <button style={s.pausedToggle} onClick={() => setPausedExpanded(e => !e)}>
                  <span>Pausierte Kampagnen ({data.paused_campaigns.length})</span>
                  <span style={{ fontSize: 11, color: '#aeaeb2' }}>{pausedExpanded ? '▲' : '▼'}</span>
                </button>
                {pausedExpanded && <CampTable campaigns={data.paused_campaigns} faded />}
              </>
            )}
          </div>

          {/* ── Bottom two-column ── */}
          <div style={s.bottomRow}>

            {/* Kundendaten */}
            <div ref={kundendatenRef} style={{ ...s.card, flex: '1 1 320px' }}>
              <div style={s.cardHeader}>
                <span style={s.cardTitle}>Kundendaten</span>
                {!editMode && (
                  <button style={s.editBtn} onClick={startEdit}>Bearbeiten</button>
                )}
              </div>
              {editMode
                ? <KundendatenEdit
                    fields={editFields} onChange={setEditFields}
                    onSave={saveEdit} onCancel={() => { setEditMode(false); setSaveError(''); }}
                    saving={saving} saveError={saveError}
                  />
                : <KundendatenView cs={cs} />
              }
            </div>

            {/* Push History */}
            <div style={{ ...s.card, flex: '1 1 280px' }}>
              <div style={s.cardHeader}>
                <span style={s.cardTitle}>Push-History</span>
                <span style={s.smallBadge}>{pushHistory.length} Einträge</span>
              </div>
              {pushHistory.length === 0 ? (
                <div style={s.empty}>Noch keine Pushes für diesen Partner</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {pushHistory.slice(0, 8).map((e, i) => (
                    <div key={i} style={s.histRow}>
                      <span style={{
                        ...s.histIcon,
                        background: e.status === 'success' ? 'rgba(52,199,89,0.12)' : 'rgba(255,59,48,0.10)',
                        color: e.status === 'success' ? '#34c759' : '#ff3b30',
                      }}>
                        {e.status === 'success' ? '✓' : '✗'}
                      </span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={s.histCamp}>{e.campaign_name || '—'}</div>
                        <div style={s.histTs}>{fmtTs(e.ts)}</div>
                      </div>
                      <span style={{
                        fontSize: 11, padding: '2px 6px', borderRadius: 4, fontWeight: 600,
                        color: e.status === 'success' ? '#34c759' : '#ff3b30',
                        background: e.status === 'success' ? 'rgba(52,199,89,0.08)' : 'rgba(255,59,48,0.07)',
                      }}>
                        {e.status === 'success' ? 'OK' : 'Fehler'}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

          </div>

          {/* ── WordPress / FluentForms ── */}
          <WordPressSection accountId={accountId} />

        </>)}
      </div>
    </Layout>
  );
}

// ─── Icons ────────────────────────────────────────────────────────────────────

function IgIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" style={{ marginRight: 4 }}>
      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
    </svg>
  );
}

function WpIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" style={{ marginRight: 5 }}>
      <path d="M21.469 6.825c.84 1.537 1.318 3.3 1.318 5.175 0 3.979-2.156 7.456-5.363 9.325l3.295-9.527c.615-1.54.82-2.771.82-3.864 0-.405-.026-.78-.07-1.109m-7.981.105c.647-.03 1.232-.105 1.232-.105.58-.075.513-.93-.067-.899 0 0-1.743.135-2.868.135-1.054 0-2.832-.15-2.832-.15-.581-.03-.648.84-.067.885 0 0 .537.06 1.117.09l1.663 4.554-2.346 7.033-3.905-11.587c.648-.03 1.233-.105 1.233-.105.58-.075.513-.93-.067-.899 0 0-1.743.135-2.868.135-.202 0-.44-.005-.692-.015C3.378 5.284 7.41 3 12 3c3.516 0 6.712 1.348 9.109 3.556-.058-.004-.116-.01-.175-.01-1.044 0-1.787.9-1.787 1.86 0 .866.502 1.597 1.035 2.465.403.703.872 1.604.872 2.909 0 .906-.348 1.953-.805 3.411l-1.056 3.528-3.824-11.374zM12 21.953c-1.149 0-2.256-.165-3.302-.472l3.507-10.188 3.592 9.84c.023.056.052.11.082.162C14.618 21.76 13.327 21.953 12 21.953zm-8.716-3.87C2.44 16.595 2 14.359 2 12c0-3.165 1.139-6.066 3.015-8.3L8.45 13.838l-5.166 4.245z"/>
    </svg>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const s: Record<string, React.CSSProperties> = {
  page: { padding: '40px 48px 72px', maxWidth: 1200, margin: '0 auto' },

  topBar: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    marginBottom: 28,
  },
  backBtn: {
    display: 'flex', alignItems: 'center', background: 'transparent', border: 'none',
    color: '#6e6e73', cursor: 'pointer', fontSize: 13, fontWeight: 500, padding: 0,
  },
  quickActions: { display: 'flex', gap: 8, alignItems: 'center' },
  qaPrimary: {
    display: 'flex', alignItems: 'center',
    background: '#1a3a5c', color: '#fff', border: 'none', borderRadius: 10,
    padding: '9px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
  },
  qaOutlineLink: {
    display: 'flex', alignItems: 'center',
    background: '#fff', color: '#1d1d1f', border: '1px solid #d2d2d7',
    borderRadius: 10, padding: '8px 14px', fontSize: 13, fontWeight: 500,
    cursor: 'pointer', textDecoration: 'none',
  },
  qaOutline: {
    display: 'flex', alignItems: 'center',
    background: '#fff', color: '#1d1d1f', border: '1px solid #d2d2d7',
    borderRadius: 10, padding: '8px 14px', fontSize: 13, fontWeight: 500, cursor: 'pointer',
  },

  partnerHeader: { marginBottom: 24 },
  title: { fontSize: 28, fontWeight: 800, color: '#1d1d1f', letterSpacing: '-0.5px', marginBottom: 8 },
  partnerMeta: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' as const },
  idBadge: {
    fontSize: 12, color: '#6e6e73', background: '#f5f5f7',
    border: '1px solid #e8e8ed', borderRadius: 6, padding: '2px 8px',
  },
  fbBadge: {
    display: 'flex', alignItems: 'center', fontSize: 12, fontWeight: 500,
    color: '#6e6e73', background: '#f5f5f7', border: '1px solid #e8e8ed', borderRadius: 6, padding: '3px 8px',
  },
  igBadge: {
    display: 'flex', alignItems: 'center', fontSize: 12, fontWeight: 500,
    color: '#6e6e73', background: '#f5f5f7', border: '1px solid #e8e8ed', borderRadius: 6, padding: '3px 8px',
  },
  warnBadge: {
    fontSize: 12, fontWeight: 600, color: '#c05000', background: 'rgba(255,149,0,0.12)',
    borderRadius: 6, padding: '3px 8px', cursor: 'pointer',
  },
  wpWarnBadge: {
    display: 'inline-flex', alignItems: 'center',
    fontSize: 12, fontWeight: 600, color: '#ff3b30', background: 'rgba(255,59,48,0.10)',
    borderRadius: 6, padding: '3px 8px',
  },

  statsRow: { display: 'flex', gap: 12, marginBottom: 18, flexWrap: 'wrap' as const },
  statCard: {
    background: '#fff', border: '1px solid #e8e8ed', borderRadius: 14,
    padding: '16px 22px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', flex: '1 1 120px',
  },
  statNum: { fontSize: 26, fontWeight: 700, color: '#1d1d1f', letterSpacing: '-0.5px' },
  statLabel: { fontSize: 11, color: '#6e6e73', marginTop: 4 },

  card: {
    background: '#fff', border: '1px solid #e8e8ed', borderRadius: 16,
    padding: '20px 24px', boxShadow: '0 1px 4px rgba(0,0,0,0.04)', marginBottom: 16,
  },
  cardHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16,
  },
  cardTitle: { fontSize: 15, fontWeight: 600, color: '#1d1d1f' },
  countBadge: {
    fontSize: 12, fontWeight: 700, color: '#1a3a5c',
    background: 'rgba(26,58,92,0.09)', borderRadius: 6, padding: '2px 8px',
  },
  smallBadge: { fontSize: 11, color: '#aeaeb2', background: '#f5f5f7', borderRadius: 6, padding: '2px 7px' },

  table: { width: '100%', borderCollapse: 'collapse' as const, fontSize: 13 },
  th: {
    padding: '8px 10px', fontSize: 11, fontWeight: 600, color: '#aeaeb2',
    textTransform: 'uppercase' as const, letterSpacing: '0.05em',
    borderBottom: '1px solid #e8e8ed',
  },
  tdName: {
    padding: '10px 10px', color: '#1d1d1f', maxWidth: 320,
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const,
  },
  td: { padding: '10px 10px', color: '#1d1d1f', textAlign: 'right' as const, fontVariantNumeric: 'tabular-nums' },

  pausedToggle: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    width: '100%', background: '#f5f5f7', border: '1px solid #e8e8ed',
    borderRadius: 10, padding: '10px 14px', cursor: 'pointer',
    fontSize: 13, fontWeight: 500, color: '#6e6e73', marginTop: 14,
  },

  bottomRow: { display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' as const },

  // KV list
  kvList: { display: 'flex', flexDirection: 'column', gap: 10 },
  kvRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, fontSize: 13 },
  kvLabel: { color: '#6e6e73', flexShrink: 0, width: 130 },
  kvVal: { color: '#1d1d1f', fontWeight: 500, textAlign: 'right' as const, flex: 1, wordBreak: 'break-all' as const },
  kvMissing: { color: '#ff3b30' },

  // Edit form
  editLabel: { display: 'block', fontSize: 11, color: '#6e6e73', marginBottom: 4, fontWeight: 500 },
  editInput: {
    width: '100%', border: '1.5px solid #d2d2d7', borderRadius: 8,
    padding: '8px 10px', fontSize: 13, color: '#1d1d1f', outline: 'none',
    background: '#fff', boxSizing: 'border-box' as const,
  },
  editBtn: {
    background: 'transparent', border: '1px solid #d2d2d7', borderRadius: 8,
    color: '#1a3a5c', padding: '5px 12px', cursor: 'pointer', fontSize: 12, fontWeight: 600,
  },
  saveBtn: {
    background: '#1a3a5c', color: '#fff', border: 'none', borderRadius: 9,
    padding: '9px 20px', cursor: 'pointer', fontSize: 13, fontWeight: 600,
  },
  cancelBtn: {
    background: '#fff', color: '#6e6e73', border: '1px solid #d2d2d7', borderRadius: 9,
    padding: '9px 16px', cursor: 'pointer', fontSize: 13,
  },
  addLocBtn: {
    background: '#f5f5f7', border: '1px solid #e8e8ed', borderRadius: 7,
    padding: '4px 10px', cursor: 'pointer', fontSize: 12, color: '#1a3a5c', fontWeight: 600,
  },
  removeLocBtn: {
    background: 'transparent', border: '1px solid #e8e8ed', borderRadius: 7,
    padding: '8px 10px', cursor: 'pointer', fontSize: 16, color: '#aeaeb2', flexShrink: 0,
    lineHeight: 1,
  },

  // Push history
  histRow: {
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '8px 0', borderBottom: '1px solid #f0f0f5',
  },
  histIcon: {
    width: 28, height: 28, borderRadius: 8, display: 'flex',
    alignItems: 'center', justifyContent: 'center', fontWeight: 700,
    fontSize: 13, flexShrink: 0,
  },
  histCamp: { fontSize: 13, fontWeight: 500, color: '#1d1d1f', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const },
  histTs: { fontSize: 11, color: '#aeaeb2', marginTop: 1 },

  miniBtn: {
    background: '#fff', border: '1px solid #d2d2d7', borderRadius: 6,
    padding: '3px 8px', cursor: 'pointer', fontSize: 11, color: '#1a3a5c', flexShrink: 0,
  },
  openLinkBtn: {
    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
    width: 36, border: '1.5px solid #d2d2d7', borderRadius: 8, color: '#1a3a5c',
    background: '#fff', textDecoration: 'none',
  },

  // Misc
  center: { display: 'flex', alignItems: 'center', padding: '60px 0' },
  spinner: {
    width: 24, height: 24, border: '2.5px solid #e8e8ed',
    borderTop: '2.5px solid #1a3a5c', borderRadius: '50%', animation: 'spin 0.7s linear infinite',
  },
  errBox: {
    background: '#fff2f2', border: '1px solid rgba(255,59,48,0.2)',
    borderRadius: 12, padding: '14px 18px', color: '#ff3b30', fontSize: 13, marginBottom: 20,
  },
  empty: { color: '#aeaeb2', fontSize: 13, padding: '12px 0' },
};
